/**
 * Hover-driven mega menu.
 *
 * Wraps a native <details>, so it stays clickable and keyboard-operable when
 * hover is unavailable (touch, assistive tech). Hover only takes over on
 * devices that actually have a pointer.
 */
class ShopMegaItem extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.closeTimer = null;
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    if (!this.details || !this.summary) return;

    this.onToggle = this.onToggle.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);

    this.details.addEventListener('toggle', this.onToggle);
    this.addEventListener('mouseenter', this.onPointerEnter);
    this.addEventListener('mouseleave', this.onPointerLeave);
    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('focusout', this.onFocusOut);
  }

  get isOpen() {
    return this.details.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;
    ShopMegaItem.closeAllExcept(this);
    this.details.setAttribute('open', '');
  }

  close() {
    if (!this.isOpen) return;
    this.details.removeAttribute('open');
  }

  /** Keeps aria-expanded truthful however the panel was opened. Dawn's stock
   *  disclosure only ever writes "false", which leaves screen readers wrong. */
  onToggle() {
    this.summary.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
  }

  onPointerEnter() {
    if (!this.hoverQuery.matches) return;
    clearTimeout(this.closeTimer);
    this.open();
  }

  onPointerLeave() {
    if (!this.hoverQuery.matches) return;
    clearTimeout(this.closeTimer);
    // Short grace period so a diagonal move into the panel doesn't close it.
    this.closeTimer = setTimeout(() => this.close(), 200);
  }

  onKeyUp(event) {
    if (event.code !== 'Escape') return;
    this.close();
    this.summary.focus();
  }

  onFocusOut() {
    // Runs before focus lands, so defer the containment check.
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.closeTimer);
  }

  static closeAllExcept(current) {
    document.querySelectorAll('shop-mega-item').forEach((item) => {
      if (item !== current && typeof item.close === 'function') item.close();
    });
  }
}

customElements.define('shop-mega-item', ShopMegaItem);
